import { useEffect, useMemo, useState } from "react";
import { Input, Modal, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { postResponseData, updateData } from "../../api/apiService";
import { CLIENTS } from "../../api/endpointMap";
import { fromApiValue, PORTAL_TO_FLAG } from "../UserAssignmentSelect";
import { useOrgUsers } from "../../context/OrgUsersContext";

// Reassign client(s) to one or more users (or to the Shared pool).
// Drives both the single-row Reassign action and the bulk-reassign flow
// from the Action Buttons row in Clients.jsx.
//
// The picker is a checkbox TABLE (S.No / User / select) with a search
// filter — a client can be assigned to MULTIPLE users (many-to-many).
// Reassign opens the same table pre-checked with the client's current
// assignees. Checking zero rows = Shared pool.
//
// Props:
//   open       — bool
//   onCancel   — () => void
//   onSuccess  — () => void  (caller refreshes its list + clears selection)
//   portal     — "income-tax" | "gst" | "tds" (URL segment for the BE route)
//   clientIds  — string[] (1 entry → single-record PUT, >1 → bulk-assign POST)
//   clientLabel — optional friendly label for the modal title
//   currentAssignedUsers — the client's current assignees (array of
//                 {user_id,...} objects or ids) used to pre-check the
//                 table on open. Bulk callers leave this empty.
//
// On submit (many-to-many):
//   single  → PUT /clients/{portal}/{id}/assign  body: { user_ids }
//   bulk    → POST /clients/{portal}/bulk-assign body: { client_ids, user_ids }

const ReassignClientsModal = ({
  open,
  onCancel,
  onSuccess,
  portal,
  clientIds = [],
  clientLabel,
  currentAssignedUsers = [],
}) => {
  // Selected user-ids (the table's checked rows). [] = Shared pool.
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { users, loaded, loading, ensureLoaded, refresh: refreshOrgUsers } =
    useOrgUsers();

  // Make sure the org users are loaded whenever the modal opens.
  useEffect(() => {
    if (open) ensureLoaded();
  }, [open, ensureLoaded]);

  // Reset on every fresh open: pre-check the current assignees (single-row
  // Reassign) or nothing (bulk / no current assignee), clear the search.
  useEffect(() => {
    if (open) {
      setSelectedIds(fromApiValue(currentAssignedUsers));
      setSearch("");
      setSubmitting(false);
    }
  }, [open, currentAssignedUsers]);

  const isBulk = clientIds.length > 1;
  const titleSuffix = clientLabel
    ? clientLabel
    : isBulk
    ? `${clientIds.length} selected clients`
    : "client";

  // Portal-scope the user list: only members with the matching
  // enable_<portal>_portal flag can own a client of that portal. Mirrors
  // the old UserAssignmentSelect behaviour.
  const portalFlagField = portal ? PORTAL_TO_FLAG[portal] || null : null;

  // Rows = assignable org users, filtered by portal access + search box.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || [])
      .filter((u) => {
        if (portalFlagField && u[portalFlagField] === false) return false;
        if (!q) return true;
        const name = (u.full_name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .map((u, idx) => ({
        key: u.id,
        sno: idx + 1,
        name: u.full_name ? `${u.full_name}` : u.email,
        email: u.email,
      }));
  }, [users, portalFlagField, search]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      width: 70,
      render: (_v, _r, index) => index + 1,
    },
    {
      title: "User",
      dataIndex: "name",
      render: (name, r) => (
        <div>
          <div className="font-medium text-gray-800">{name}</div>
          {r.email && r.email !== name && (
            <div className="text-xs text-gray-500">{r.email}</div>
          )}
        </div>
      ),
    },
  ];

  const handleOk = async () => {
    if (clientIds.length === 0) {
      toast.error("No clients selected");
      return;
    }
    setSubmitting(true);
    const userIds = selectedIds; // [] for Shared, ids otherwise.
    try {
      if (isBulk) {
        const result = await postResponseData(CLIENTS.BULK_ASSIGN(portal), {
          client_ids: clientIds,
          user_ids: userIds,
        });
        const successCount = result?.success ?? 0;
        const skipped = Array.isArray(result?.skipped) ? result.skipped : [];
        if (skipped.length > 0) {
          toast.info(
            `Reassigned ${successCount}; ${skipped.length} skipped (not in scope).`
          );
        } else {
          toast.success(`Reassigned ${successCount} clients.`);
        }
      } else {
        await updateData(CLIENTS.ASSIGN(portal, clientIds[0]), {
          user_ids: userIds,
        });
        toast.success("Client reassigned.");
      }
      refreshOrgUsers();
      onSuccess?.();
    } catch (err) {
      const msg =
        err?.detail ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to reassign";
      toast.error(typeof msg === "string" ? msg : "Failed to reassign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Reassign ${titleSuffix}`}
      onCancel={onCancel}
      onOk={handleOk}
      okText={isBulk ? "Reassign all" : "Reassign"}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
      maskClosable={!submitting}
      width={560}
    >
      <div className="mb-3 text-gray-600 text-sm">
        Select the team members who should own {isBulk ? "these clients" : "this client"}.
        You can pick more than one. Leave all unchecked to make
        {isBulk ? " them" : " it"} Shared (no one).
      </div>
      <Input
        allowClear
        prefix={<SearchOutlined className="text-gray-400" />}
        placeholder="Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      />
      <Table
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading || !loaded}
        pagination={rows.length > 8 ? { pageSize: 8, size: "small" } : false}
        scroll={{ y: 280 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys),
          // Let a click anywhere on the row toggle its checkbox.
        }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedIds((prev) =>
              prev.includes(record.key)
                ? prev.filter((k) => k !== record.key)
                : [...prev, record.key]
            );
          },
        })}
        locale={{ emptyText: "No matching users" }}
      />
      <div className="mt-2 text-xs text-gray-500">
        {selectedIds.length === 0
          ? "Shared (no one assigned)"
          : `${selectedIds.length} user${selectedIds.length > 1 ? "s" : ""} selected`}
      </div>
    </Modal>
  );
};

export default ReassignClientsModal;
